import React, { useState, useEffect } from 'react';
import api from "../lib/api";
import { toast } from 'sonner';
import { FiPlay, FiPause, FiActivity, FiTrendingUp, FiTrendingDown, FiDownload } from 'react-icons/fi';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useWebSocket } from '../hooks/useWebSocket';
export default function Overview() {
  const [botStatus, setBotStatus] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isConnected, lastMessage } = useWebSocket();

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (lastMessage?.type === 'trade_executed') {
      const newTrade = lastMessage.data;
      setTrades(prev => [newTrade, ...prev.slice(0, 9)]);
      toast.success(`Trade Executed: ${newTrade.side.toUpperCase()} ${newTrade.symbol}`, {
        description: `Price: $${newTrade.price.toFixed(2)} | ${newTrade.is_paper ? 'PAPER' : 'LIVE'}`
      });
      fetchData();
    }
  }, [lastMessage]);

  const fetchData = async () => {
    try {
      const [statusRes, pnlRes, tradesRes] = await Promise.all([
        api.get('/bot/status'),
        api.get('/trades/pnl'),
        api.get('/trades')
      ]);
      setBotStatus(statusRes.data);
      setPnl(pnlRes.data);
      setTrades(tradesRes.data.slice(0, 10));
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const toggleBot = async () => {
    try {
      if (botStatus?.is_running) {
        await api.post('/bot/stop');
        toast.success('Bot stopped - autonomous trading halted');
      } else {
        await api.post('/bot/start');
        toast.success('Bot started - autonomous trading active!', {
          description: 'Bot will now trade automatically based on your configured pairs and strategies'
        });
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle bot');
    }
  };

  const downloadTrades = () => {
    const csv = [
      ['Symbol', 'Side', 'Amount', 'Price', 'Total', 'Type', 'Status', 'Date'],
      ...trades.map(t => [
        t.symbol,
        t.side,
        t.amount,
        t.price,
        t.total,
        t.is_paper ? 'PAPER' : 'LIVE',
        t.status,
        new Date(t.executed_at).toLocaleString()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trades_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Trades exported successfully');
  };

  if (loading) {
    return <div className="text-text-muted">Loading dashboard...</div>;
  }

  return (
    <div data-testid="overview-dashboard" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight text-text-main">DASHBOARD OVERVIEW</h1>
          <div className="flex items-center gap-2 mt-1">
            <div className={`h-2 w-2 rounded-full ${isConnected ? 'bg-secondary animate-pulse' : 'bg-text-muted'}`} />
            <span className="text-xs text-text-muted">
              {isConnected ? 'Real-time data connected' : 'Connecting...'}
            </span>
          </div>
        </div>
        <button
          data-testid="bot-toggle-button"
          onClick={toggleBot}
          className={`font-bold uppercase tracking-wider text-xs px-6 py-3 rounded-sm transition-all shadow-lg ${botStatus?.is_running
              ? 'bg-destructive/10 text-destructive border border-destructive/50 hover:bg-destructive/20 shadow-destructive/20'
              : 'bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-secondary/20'
            }`}
        >
          {botStatus?.is_running ? (
            <>
              <FiPause className="inline mr-2" /> STOP BOT
            </>
          ) : (
            <>
              <FiPlay className="inline mr-2" /> START BOT
            </>
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-surface border border-border/40 rounded-sm p-6 hover:border-primary/30 transition-all">
          <div className="text-xs uppercase tracking-widest text-text-muted font-bold mb-3">Bot Status</div>
          <div className="flex items-center gap-3">
            <FiActivity
              className={botStatus?.is_running ? 'text-secondary animate-pulse' : 'text-text-muted'}
              size={28}
            />
            <span
              className={`text-2xl font-mono font-bold ${botStatus?.is_running ? 'text-secondary' : 'text-text-muted'
                }`}
              data-testid="bot-status-indicator"
            >
              {botStatus?.is_running ? 'ACTIVE' : 'INACTIVE'}
            </span>
          </div>
        </div>

        <div className="bg-surface border border-border/40 rounded-sm p-6 hover:border-primary/30 transition-all">
          <div className="text-xs uppercase tracking-widest text-text-muted font-bold mb-3">Total P&L</div>
          <div className="flex items-center gap-3">
            {pnl?.total_pnl >= 0 ? (
              <FiTrendingUp className="text-secondary" size={28} />
            ) : (
              <FiTrendingDown className="text-destructive" size={28} />
            )}
            <span
              className={`text-2xl font-mono font-bold ${pnl?.total_pnl >= 0 ? 'text-secondary' : 'text-destructive'
                }`}
              data-testid="total-pnl-value"
            >
              ${pnl?.total_pnl?.toFixed(2) || '0.00'}
            </span>
          </div>
        </div>

        <div className="bg-surface border border-border/40 rounded-sm p-6 hover:border-primary/30 transition-all">
          <div className="text-xs uppercase tracking-widest text-text-muted font-bold mb-3">Total Trades</div>
          <span className="text-3xl font-mono font-bold text-text-main" data-testid="total-trades-value">
            {pnl?.total_trades || 0}
          </span>
          <div className="text-xs text-text-muted mt-2">
            <span className="text-secondary">{pnl?.winning_trades || 0}</span> wins ·{' '}
            <span className="text-destructive">{pnl?.losing_trades || 0}</span> losses
          </div>
        </div>

        <div className="bg-surface border border-border/40 rounded-sm p-6 hover:border-primary/30 transition-all">
          <div className="text-xs uppercase tracking-widest text-text-muted font-bold mb-3">Win Rate</div>
          <span className="text-3xl font-mono font-bold text-primary" data-testid="win-rate-value">
            {pnl?.win_rate?.toFixed(1) || '0.0'}%
          </span>
        </div>
      </div>

      <div className="bg-surface border border-border/40 rounded-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold uppercase tracking-widest font-heading">Recent Trades</h2>
          <button
            onClick={downloadTrades}
            disabled={trades.length === 0}
            className="flex items-center gap-2 px-4 py-2 text-xs uppercase font-bold bg-primary/10 text-primary border border-primary/20 rounded-sm hover:bg-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <FiDownload /> Export CSV
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="recent-trades-table">
            <thead>
              <tr className="border-b border-border text-text-muted">
                <th className="text-left py-3 px-3 uppercase text-xs font-bold">Symbol</th>
                <th className="text-left py-3 px-3 uppercase text-xs font-bold">Side</th>
                <th className="text-right py-3 px-3 uppercase text-xs font-bold">Amount</th>
                <th className="text-right py-3 px-3 uppercase text-xs font-bold">Price</th>
                <th className="text-right py-3 px-3 uppercase text-xs font-bold">Total</th>
                <th className="text-center py-3 px-3 uppercase text-xs font-bold">Type</th>
                <th className="text-center py-3 px-3 uppercase text-xs font-bold">Status</th>
                <th className="text-right py-3 px-3 uppercase text-xs font-bold">Time</th>
              </tr>
            </thead>
            <tbody>
              {trades.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-12 text-text-muted">
                    <div className="flex flex-col items-center gap-3">
                      <FiActivity size={40} className="opacity-30" />
                      <div>
                        <p className="font-medium">No trades yet</p>
                        <p className="text-xs mt-1">Start the bot to begin autonomous trading</p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                trades.map((trade) => (
                  <tr key={trade.trade_id} className="border-b border-border/50 hover:bg-white/5 transition-colors">
                    <td className="py-3 px-3 font-mono font-bold text-text-main">{trade.symbol}</td>
                    <td className="py-3 px-3">
                      <span
                        className={`text-xs font-bold uppercase px-2 py-1 rounded ${
                          trade.side === 'buy'
                            ? 'bg-secondary/10 text-secondary border border-secondary/20'
                            : 'bg-destructive/10 text-destructive border border-destructive/20'
                        }`}
                      >
                        {trade.side}
                      </span>
                    </td>
                    <td className="py-3 px-3 font-mono text-right text-text-main">
                      {trade.amount?.toFixed(8)}
                    </td>
                    <td className="py-3 px-3 font-mono text-right text-text-main">
                      ${trade.price?.toFixed(2)}
                    </td>
                    <td className="py-3 px-3 font-mono text-right text-text-main font-medium">
                      ${trade.total?.toFixed(2)}
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span
                        className={`text-xs uppercase px-2 py-1 rounded ${
                          trade.is_paper ? 'bg-accent-orange/10 text-accent-orange' : 'bg-primary/10 text-primary'
                        }`}
                      >
                        {trade.is_paper ? 'PAPER' : 'LIVE'}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-center text-text-muted uppercase text-xs">{trade.status}</td>
                    <td className="py-3 px-3 text-right text-text-muted text-xs">
                      {new Date(trade.executed_at).toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
